import { gql, useQuery } from '@apollo/client'
import { Grid, Typography } from '@mui/material'
import {
  Asset,
  Campaign,
  Query,
  Retailer,
} from '@promoboxx/graphql-gateway-types'
import I18n from 'i18n-js'
import { useMemo } from 'react'

import {
  CampaignCardSkeleton,
  CampaignCardTruncatedDescription,
} from '../CampaignCard'
import { OmniCampaignCard } from '../OmniCampaignCard'

import { OrganicCampaignDetailsFilters } from './organicCampaignDetailsFilter'

interface OrganicCampaignDetailsAssetsProps {
  campaignId: Campaign['id']
  shareType: string
  filterApi: OrganicCampaignDetailsFilters
  retailerId?: Retailer['id'] | null
}

const OrganicCampaignDetailsAssets: React.FC<
  OrganicCampaignDetailsAssetsProps
> = (props) => {
  const { data, loading } = useQuery<Query>(GET_CAMPAIGN_ASSETS_QUERY, {
    variables: {
      campaignId: props.campaignId,
      retailer_id: props.retailerId,
    },
    skip: props.retailerId == null,
  })

  const assets = useMemo(() => {
    const sorted = [...(data?.getCampaign?.assets || [])]

    sorted.sort((a, b) => {
      if (props.filterApi.debouncedFilterInfo.filter.sortField === 'created') {
        return (
          new Date(a.created_at || '').valueOf() -
          new Date(b.created_at || '').valueOf()
        )
      }

      if (props.filterApi.debouncedFilterInfo.filter.sortField === 'updated') {
        return (
          new Date(a.updated_at || '').valueOf() -
          new Date(b.updated_at || '').valueOf()
        )
      }

      if (props.filterApi.debouncedFilterInfo.filter.sortField === 'name') {
        return (a.digital_asset?.name || '').localeCompare(
          b.digital_asset?.name || '',
        )
      }

      if (props.filterApi.debouncedFilterInfo.filter.sortField === 'format') {
        return (
          a.digital_asset?.file?.format?.localeCompare(
            b.digital_asset?.file?.format || '',
          ) || 0
        )
      }

      return 0
    })

    if (props.filterApi.debouncedFilterInfo.filter.sortDirection === 'desc') {
      sorted.reverse()
    }

    const assets: Asset[] = []

    for (const asset of sorted) {
      if (!asset.enabled || !asset.digital_asset) {
        continue
      }

      const tiktokEligible =
        asset.digital_asset.eligible_share_types?.tiktok || {}

      if (props.shareType === 'tiktok') {
        if (
          tiktokEligible.photo ||
          tiktokEligible.link ||
          tiktokEligible.video
        ) {
          assets.push(asset)
        }
      } else {
        assets.push(asset)
      }
    }

    return assets
  }, [
    data?.getCampaign?.assets,
    props.filterApi.debouncedFilterInfo.filter.sortDirection,
    props.filterApi.debouncedFilterInfo.filter.sortField,
    props.shareType,
  ])

  const { filteredShares, alreadySharedIds } = useMemo(() => {
    const assetIds = assets.map((x) => x.id)
    // We need to filter by the assets we are actually rendering, to handle
    // asset vs tiktok shares.
    const filteredShares = (data?.getShares?.data || []).filter((x) =>
      assetIds.includes(x.asset?.id || ''),
    )
    const alreadySharedIds = data?.getShares?.data.map((x) => x.asset?.id)

    return {
      filteredShares,
      alreadySharedIds,
    }
  }, [assets, data?.getShares?.data])

  if (loading || !props.retailerId) {
    return (
      <>
        <Grid container justifyContent="center" spacing={4}>
          <Grid item xs={12} sm={6} md={6} lg={4}>
            <CampaignCardSkeleton />
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={4}>
            <CampaignCardSkeleton />
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={4}>
            <CampaignCardSkeleton />
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={4}>
            <CampaignCardSkeleton />
          </Grid>
        </Grid>
      </>
    )
  }

  return (
    <>
      <Grid container justifyContent="center" spacing={4}>
        {assets.map((x) => {
          const wasAlreadyShared = alreadySharedIds?.includes(x.id) || false

          return (
            <Grid item xs={12} sm={6} md={6} lg={4} key={x.id}>
              <OmniCampaignCard
                id={x.id}
                title={x.name}
                description={
                  <CampaignCardTruncatedDescription>
                    {x.description}
                  </CampaignCardTruncatedDescription>
                }
                mediaPublicId={x.digital_asset?.file?.public_id}
                mediaType={x.digital_asset?.file?.resource_type}
                actionAreaLabel={I18n.t('downloadables.download')}
                actionAreaColor={
                  props.shareType === 'tiktok' ? 'tiktok' : 'primary'
                }
                dim={wasAlreadyShared}
                actionAreaInvert={wasAlreadyShared}
              />
            </Grid>
          )
        })}
      </Grid>

      {filteredShares.length > 0 ? (
        <>
          <Typography variant="h6" sx={{ marginY: 4 }}>
            {I18n.t('content.organicCampaignDetails.previouslyShared')}
          </Typography>

          <Grid container justifyContent="center" spacing={4}>
            {filteredShares.map((x) => {
              return (
                <Grid item xs={12} sm={6} md={6} lg={4}>
                  <OmniCampaignCard
                    key={x.id}
                    title={x.asset?.name}
                    description={
                      <>
                        <CampaignCardTruncatedDescription>
                          {x.asset?.description}
                        </CampaignCardTruncatedDescription>
                      </>
                    }
                    mediaPublicId={x.asset?.digital_asset?.file?.public_id}
                    mediaType={x.asset?.digital_asset?.file?.resource_type}
                    actionAreaLabel={I18n.t('shared.view')}
                    actionAreaColor={
                      props.shareType === 'tiktok' ? 'tiktok' : 'primary'
                    }
                    actionAreaInvert
                  />
                </Grid>
              )
            })}
          </Grid>
        </>
      ) : undefined}
    </>
  )
}

const GET_CAMPAIGN_ASSETS_QUERY = gql`
  query getCampaignAssetsOrganicDetails($campaignId: ID!, $retailer_id: ID!) {
    getCampaign(
      campaignId: $campaignId
      params: { fields: ["assets.digital_asset.eligible_share_types"] }
    ) {
      id

      assets {
        id
        enabled

        name
        description

        created_at
        updated_at

        digital_asset {
          id

          name

          file_url
          file {
            id
            public_id
            bytes
            width
            height
            resource_type
          }

          eligible_share_types {
            tiktok {
              link
              photo
              video
            }
          }
        }
      }
    }

    getShares(
      params: {
        retailer_id: $retailer_id
        campaign_id: $campaignId
        fields: ["asset", "asset.digital_asset"]
        channels: ["asset"]
      }
    ) {
      data {
        id
        status
        created_at
        scheduled_for

        asset {
          id
          enabled

          name
          description

          created_at
          updated_at

          digital_asset {
            id

            name

            file_url
            file {
              id
              public_id
              bytes
              width
              height
              resource_type
            }
          }
        }
      }
    }
  }
`

export default OrganicCampaignDetailsAssets
